import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import tw, { styled } from 'twin.macro';
import classNames from 'classnames';

import { useAuth } from '../../hooks/use-auth';
import { useSite } from '../../hooks/use-site';
import { OrderStatus } from '../../entities/order';
import ContainerMd from '../../components/common/containers/containerMd';
import RoundedBlock from '../../components/common/blocks/roundedBlock';
import Heading3 from '../../components/common/headings/heading3';
import { parameters, strings } from '../../localization/strings';
import OrderProgressComponent from '../../components/order/progress';
import { getDate } from '../../services/formatter/date';
import { getLocalizedPath } from '../../helpers/localization-helper';
import StickyBanner from '../../components/common/sticky/stickyBanner';
import CommonButtonDefault from '../../components/common/buttons/default';
import * as ordersApi from '../../services/api/orders';

const LinkButton = styled(Link)`
  ${tw`inline-block p-2 mb-3 pl-5 pr-5 bg-blue-800 hover:bg-blue-900 text-gray-100 text-md md:text-lg rounded-button focus:border-4 border-blue-300`}
`;

export default function ProfileDashboardPageComponent() {
  const { user, token, tokenAdmin } = useAuth();
  const { isSelfCo, locale, region } = useSite();
  const [disabled, setDisabled] = useState<boolean>(false);

  const currentStep = (status: string, surveyFinished?: boolean) => {
    switch (status) {
      case OrderStatus.Completed:
        return 4;
      case OrderStatus.SampleReceived:
        return 3;
      default:
        // For questionnaireAfterSale mode, step 1 is "Complete questionnaire"
        // If survey not finished, stay at step 1; otherwise step 2
        if (parameters().features.questionnaireAfterSale && surveyFinished === false) {
          return 1;
        }

        return 2;
    }
  };

  const downloadPdf = async (pdfId: string | undefined) => {
    if (pdfId) {
      setDisabled(true);
      const res = await ordersApi.getPdf(token || tokenAdmin, pdfId);

      if (res) {
        setDisabled(false);
        window.location.href = res;
      }
    }
  };

  return (
    <div className="mt-10 mx-auto px-4 md:max-w-3xl">
      {!user?.orders.length && <div>{strings().dashboard.noOrders}</div>}
      {user?.orders.map((order, i) => {
        return (
          <RoundedBlock key={order.id} className="mb-5">
            <div className="flex flex-col sm:flex-row">
              <div className="md:mr-auto order-last sm:order-1">
                {order.productKey && <Heading3>{order.productTitle}</Heading3>}
                <div className="sm:flex sm:space-x-3 mt-3">
                  {order.id && (
                    <p className="text-content-700 text-sm mt-1 sm:mt-0">
                      {strings().dashboard.orderId} {order.id}
                    </p>
                  )}
                  {order.paidAt && (
                    <p className="text-content-700 text-sm mt-1 sm:mt-0">
                      {strings().dashboard.orderedAt} {getDate(order.paidAt)}
                    </p>
                  )}
                  {(order.firstName ?? order.lastName) && (
                    <p className="text-content-700 text-sm">
                      {order.firstName} {order.lastName}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex-shrink-1 order-first sm:order-2 mb-3">
                <span
                  className={classNames('inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium', {
                    'bg-green-100 text-green-800': order.status === OrderStatus.Completed,
                    'bg-yellow-100 text-yellow-800': order.status !== OrderStatus.Completed,
                  })}
                >
                  {order.status === OrderStatus.Completed &&
                    order.productKey !== 'TREATMENT_PLAN' &&
                    strings().dashboard.status.completed}

                  {order.status === OrderStatus.Completed && order.productKey === 'TREATMENT_PLAN' && 'Aktyvuotas'}
                  {order.status !== OrderStatus.Completed && strings().dashboard.status.ongoing}
                </span>
              </div>
            </div>
            {order.productId !== parameters().product.immunotherapyProductId &&
              order.productId !== parameters().product.immunotherapyProductId6month &&
              !order.isGift && (
                <div className="mt-10">
                  <OrderProgressComponent
                    isBundle={order.productKey === 'BUNDLE_SELFCO'}
                    isConsultation={order.productKey === 'CONSULTATION'}
                    current={currentStep(order.status, order.surveyFinished)}
                    isResearch={order.productKey === 'FREE_SCREENER' || order.productKey === 'FREE_SCREENER_ASIT'}
                    isImmunotherapy={order.productId === parameters().product.stepsImmunotherapyId}
                    isImmunotherapyConsultation={
                      order.productId === parameters().product.stepsImmunotherapyConsultationId
                    }
                  />
                </div>
              )}

            {order.status === OrderStatus.Completed &&
              order.productKey !== 'TREATMENT_PLAN' &&
              order.productKey !== 'BUNDLE_SELFCO' && (
                <RoundedBlock className="bg-gray-50 mt-10" key={`registration-${i + 1}`}>
                  <div>
                    <Heading3 className="mb-3">{strings().dashboard.tasks.orderCompletedHeading}</Heading3>
                    <p className="text-content-700">
                      {order.productKey === 'CONSULTATION'
                        ? strings().dashboard.tasks.orderCompletedDescriptionConsultation
                        : strings().dashboard.tasks.orderCompletedDescription}
                    </p>
                  </div>
                  <div className="mt-8">
                    <LinkButton
                      to={`/profile/results/${order.hash}${
                        order.productKey === 'CONSULTATION' ? '/recommendations' : ''
                      }`}
                      className="no-underline"
                    >
                      {strings().dashboard.resultPreview}
                    </LinkButton>
                  </div>
                </RoundedBlock>
              )}

            {order.status !== OrderStatus.Completed &&
              !order.isGift &&
              !order.surveyFinished &&
              order.extraSurveyId &&
              order.productId && (
                <RoundedBlock className="bg-gray-50 mt-10" key={`questionnaire-${i + 1}`}>
                  <div>
                    <Heading3 className="mb-3">{strings().FOOD_ID.test_answer.emptyStateRecommendationsTitle}</Heading3>
                    <p className="text-content-700">{strings().FOOD_ID.test_answer.emptyStateRecommendationsText}</p>
                  </div>
                  <div className="mt-8">
                    <LinkButton
                      to={getLocalizedPath(
                        `/questionnaire/patient?orderHash=${order.hash}&productId=${order.productId}&surveyId=${order.extraSurveyId}`,
                        locale,
                        region,
                      )}
                      className="no-underline"
                    >
                      {strings().checkout.fillQuestionnaire}
                    </LinkButton>
                  </div>
                </RoundedBlock>
              )}

            {order.isGift && order.pdfId && (
              <RoundedBlock className="bg-gray-50 mt-10" key={`registration-${i + 1}`}>
                <div>
                  <Heading3 className="mb-3">{strings().dashboard.tasks.giftCouponReady}</Heading3>
                </div>
                <div className="flex items-center">
                  <CommonButtonDefault disabled={disabled} sm primary onClick={() => downloadPdf(order.pdfId)}>
                    {strings().button.download}
                  </CommonButtonDefault>
                </div>
              </RoundedBlock>
            )}
          </RoundedBlock>
        );
      })}
      <StickyBanner providedFeedback={user?.providedFeedback} hasCompletedOrders={user?.hasCompletedOrders} />
    </div>
  );
}
